'use client';
import { useEffect, useRef } from 'react';
import { ETSY_URL, GUMROAD_URL, INFINITEMARKET_URL } from '@/data/bundles';

export default function FinalCTASection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('reveal-hidden');
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="relative py-24 lg:py-32 overflow-hidden" aria-labelledby="final-cta-heading">
      {/* Background layers */}
      <div
        className="absolute inset-0 opacity-10"
        style={{ background: 'linear-gradient(135deg, rgb(79,70,229), rgb(124,58,237) 40%, rgb(236,72,153))' }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-section-dark" style={{ opacity: 0.85 }} aria-hidden="true" />
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="blob absolute bg-primary" style={{ width: '600px', height: '600px', top: '-20%', left: '-10%', opacity: 0.08 }} />
        <div className="blob blob-2 absolute bg-accent" style={{ width: '500px', height: '500px', bottom: '-20%', right: '-10%', opacity: 0.07 }} />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="reveal-hidden" ref={ref}>
          <h2
            id="final-cta-heading"
            className="section-heading mb-4"
            style={{ color: 'var(--foreground)', fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}
          >
            Stop Starting From Zero.
          </h2>
          <p className="text-xl mb-6 max-w-2xl mx-auto leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            Get one massive resource library for your next design, publishing, development, content or business project.
          </p>

          {/* Price Badge */}
          <div
            className="inline-flex items-center gap-4 glass rounded-2xl px-8 py-5 mb-8"
            style={{ border: '1px solid rgba(124,58,237,0.2)' }}
          >
            <div>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>43 Premium Collections</div>
              <div className="text-4xl font-extrabold text-gradient">$79</div>
              <div className="text-sm" style={{ color: 'var(--muted-foreground)' }}>One-Time Purchase</div>
            </div>
          </div>

          {/* Main CTA */}
          <div className="flex flex-col items-center gap-4 mb-10">
            <a
              href={ETSY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-lg py-5 px-10"
            >
              🚀 Get the Complete Bundle
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width={20} height={20}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </div>

          {/* Platform Links */}
          <div>
            <p className="text-sm mb-4" style={{ color: 'var(--muted-foreground)' }}>Choose your preferred platform:</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a href={ETSY_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm py-3 px-5">
                🛍️ Etsy — $79
              </a>
              <a href={GUMROAD_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm py-3 px-5">
                ⚡ Gumroad — $79
              </a>
              <a href={INFINITEMARKET_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm py-3 px-5">
                🌐 InfiniteMarket — $79
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
